import "twin.macro";

import { useState } from "react";
import { useParams } from "react-router";

import { CloseRounded, Edit, FilterNoneOutlined } from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Tooltip,
} from "@mui/material";

import {
  FixedHeightScrollLastChild,
  OpaqueCard,
} from "@components/StyledComponents";
import Filters, { useFilterParams } from "@features/filters";
import { RequestReportButton, useReport } from "@features/reports";
import { Title } from "@features/ui";
import IndexTable, { Column } from "@features/ui/indexTable";
import { DemandPlanVariant } from "@models/DemandPlanVariant";
import DocTitle from "@utility/DocTitle";

import { DemandPlanForm } from "../DemandPlanForm";
import { useDemandPlanQuery } from "../data/demandPlanQueries";
import { useDemandPlanVariantListQuery } from "../data/demandPlanVariantQueries";
import { DemandPlanSummaryInfo } from "./DemandPlanSummary";
import { columns as variantColumns } from "./columns/DemandPlanVariantColumns";
import { WideTooltip } from "./columns/DemandPlanVariantTooltips";
import { EditingProvider } from "./editor/EditingProvider";
import "./styles/DemandPlanVariant.css";

export const DemandPlanVariants = () => {
  const { planId } = useParams();

  const [filters] = useFilterParams();
  const filterParams = {
    skipPagination: true,
    filter: {
      demandPlanId: planId,
      searchTerm: filters.q,
      supplierIds: filters.suppliers,
      programIds: filters.programs,
      brandIds: filters.brands,
      status: filters.status,
      groupsIds: filters.groups,
    },
    sort: filters.sort,
  };

  // Load in the parent demandplan for additional detail
  const {
    data: plan,
    error: planError,
    isLoading: isPlanLoading,
  } = useDemandPlanQuery(planId);

  // Editing flag for editing demand plan record
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const handleCloseEditingPlan = () => {
    setIsEditingPlan(false);
  };

  // Demand Plan Variant Data Call
  //TODO don't use pagination? Also impacts header totals
  const {
    data = [],
    error,
    isLoading,
    ...otherQueryProps
  } = useDemandPlanVariantListQuery(filterParams);

  const dataError = error || planError;

  //Export
  useReport("demand-plan-variants", ["demand-plan-variants", filterParams]);

  const columns: Column<DemandPlanVariant>[] = variantColumns;

  return (
    <FixedHeightScrollLastChild>
      <DocTitle title={`Demand Planning | ${plan && plan.name}`} />
      <div tw="flex">
        <Title backUrl="/purchasing/demand-planning">
          <EditableTitle
            loading={isPlanLoading}
            onClick={() => setIsEditingPlan(true)}
          >
            {plan && (
              <>
                <span>{plan.name}</span>
                <WideTooltip
                  title={
                    <div tw="text-sm p-2">
                      <span>Order Windows:</span>
                      <ul tw="list-disc list-inside">
                        {plan.orderWindows.map((ow) => {
                          return <li key={ow.id}>{ow.name}</li>;
                        })}
                      </ul>
                    </div>
                  }
                >
                  <FilterNoneOutlined tw="ml-3 relative -top-0.5 mr-2" />
                </WideTooltip>
              </>
            )}
          </EditableTitle>
        </Title>
        {plan && (
          <div tw="text-sm flex-1 flex flex-row-reverse">
            <DemandPlanSummaryInfo demandPlanId={planId} />
          </div>
        )}
      </div>
      {!dataError && (
        <div tw="flex justify-between mt-3!">
          <Filters
            searchTitle="Search..."
            slots={[
              "programs",
              "suppliers",
              "demandPlanVariantStatus",
              "brands",
              "groups",
            ]}
            alwaysShow={["programs", "suppliers"]}
            defaultValues={{ sort: "item" }}
            slotProps={{
              programs: {
                skipsPagination: true,
                requestParams: {
                  filter: {
                    // Only show orderwindows that the demand plan is associated with
                    orderWindowIds: plan?.orderWindows.map((ow) => ow.id),
                  },
                },
              },
            }}
          />
          <RequestReportButton
            tw="self-end h-10"
            reportName="demand-plan-variants"
          />
        </div>
      )}

      <OpaqueCard tw="p-0 overflow-hidden relative">
        <DemandPlanVariantTable
          columns={columns}
          isLoading={isLoading}
          data={data}
          dataError={dataError}
          {...otherQueryProps}
        />
      </OpaqueCard>
      {/* Dialog window for editing the Demand Plan */}
      {plan && (
        <Dialog
          open={isEditingPlan}
          fullWidth
          maxWidth="sm"
          onClose={handleCloseEditingPlan}
        >
          <DialogTitle tw="flex justify-between items-start">
            Edit: {plan.name}
            <Tooltip title="Close">
              <IconButton onClick={handleCloseEditingPlan} edge="end">
                <CloseRounded />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent tw="pb-0">
            <DemandPlanForm
              planId={planId}
              onSaveSuccess={handleCloseEditingPlan}
            />
          </DialogContent>
        </Dialog>
      )}
    </FixedHeightScrollLastChild>
  );
};

const DemandPlanVariantTable = ({
  data,
  columns,
  dataError,
  isLoading,
  isFetching,
}) => {
  return (
    <EditingProvider data={data} isFetching={isFetching}>
      <IndexTable
        isLoading={isLoading}
        tableSize="small"
        rows={data}
        columns={columns}
        error={dataError}
      />
    </EditingProvider>
  );
};

// A Component that provides a visual and edit indivator allowing a user to initiate editing of value(s)
const EditableTitle = ({ children, onClick, loading }) => {
  if (loading) {
    return <Skeleton variant="text" width={325} height={40} />;
  }

  return (
    <div className="group" tw="cursor-pointer" onClick={onClick}>
      <span tw="align-middle group-hover:underline">{children}</span>
      <Edit className="ml-2 transition transition-discrete opacity-0 group-hover:opacity-100 delay-250" />
    </div>
  );
};
